import { useMemo } from "react";
import {
  ReactFlow, Background, MarkerType, Position, Handle,
  type Edge, type Node, type NodeTypes,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { GitBranch, Brain, Webhook, Database } from "lucide-react";
import { cls } from "../../lib/cls";
import { ExpandableGraphFrame } from "./ExpandableGraphFrame";
import type { Graph3DNode, Graph3DLink } from "./Graph3D";

/* Agentic mesh — every FoyrMind agent we ship, the protocol it
 * exposes, and the wires that connect them. CLAUDE.md §3 + the
 * A2A/AG-UI/MCP triad: a) cockpit talks AG-UI to the supervisor,
 * b) supervisor calls sub-agents via ADK transfer_to_agent (rendered
 * here as A2A internal), c) any external MCP client (Claude, Cursor)
 * sees the same tools via tools/list.
 *
 * Each agent below corresponds to a real Python module in
 * `agent/foyr_agent/agents/*.py` — there are no mock or future
 * agents on this canvas.
 */

type Slot = "external" | "supervisor" | "specialist" | "store" | "protocol";

interface MeshNode {
  id: string;
  label: string;
  sub: string;
  slot: Slot;
  protocol?: "A2A" | "AG-UI" | "MCP" | "SPARQL" | "Webhook" | null;
}

interface MeshLink {
  from: string;
  to: string;
  label: string;
  tone?: "primary" | "neutral" | "ai";
}

const NODES: readonly MeshNode[] = [
  // External entry points
  { id: "cockpit",          label: "Cockpit",                 sub: "foyrmind.hypergraphmind.com",          slot: "external", protocol: "AG-UI" },
  { id: "claude",           label: "Claude Desktop / Code",   sub: "mcp.hypergraphmind.com",               slot: "external", protocol: "MCP" },
  { id: "n8nIn",            label: "Dynamic integration layer", sub: "n8n · 400+ apps · Gmail · Drive · WhatsApp",   slot: "external", protocol: "Webhook" },

  // Protocols
  { id: "agui",             label: "AG-UI",                   sub: "SSE · RUN_STARTED · TOOL_CALL_*",      slot: "protocol", protocol: "AG-UI" },
  { id: "a2a",              label: "A2A AgentCard",           sub: ".well-known/agent.json · tasks/send",  slot: "protocol", protocol: "A2A" },
  { id: "mcp",              label: "MCP /mcp",                sub: "tools/list · tools/call · resources",  slot: "protocol", protocol: "MCP" },

  // Supervisor
  { id: "supervisor",       label: "ADK Supervisor",          sub: "orchestrator.py · LiteLLM router",     slot: "supervisor" },

  // 12 specialists (all real modules — Canvas agent ships TS substrate
  // web/src/lib/agent/* + Py mirror agent/foyr_agent/agent/types.py
  // alongside the rule layer).
  { id: "canvas",           label: "Canvas agent",            sub: "agent/types.py · ghost-suggestion bus", slot: "specialist" },
  { id: "constraint",       label: "Constraint agent",        sub: "agents/constraint.py · rule canvas",   slot: "specialist" },
  { id: "narrator",         label: "Narrator agent",          sub: "agents/narrator.py · PROV-O walks",    slot: "specialist" },
  { id: "substitution",     label: "Substitution agent",      sub: "agents/substitution.py · weighted",    slot: "specialist" },
  { id: "project_tasks",    label: "Project & Tasks",         sub: "agents/project_tasks.py · OR-Tools",   slot: "specialist" },
  { id: "email_drafter",    label: "Email Drafter",           sub: "agents/email_drafter.py · Self-Refine",slot: "specialist" },
  { id: "scheduling",       label: "Scheduling",              sub: "agents/scheduling.py · ReAct",          slot: "specialist" },
  { id: "website",          label: "Website lead",            sub: "agents/website.py · 3-role supervisor",slot: "specialist" },
  { id: "estimate",         label: "Estimate / Invoice",      sub: "agents/estimate.py · Decimal + PDF",   slot: "specialist" },
  { id: "search",           label: "Hybrid Search",           sub: "agents/search.py · RRF + ColBERT",     slot: "specialist" },
  { id: "voice",            label: "Voice",                   sub: "agents/voice.py · Pipecat",            slot: "specialist" },
  { id: "change_order",     label: "Change Order saga",       sub: "agents/change_order.py · n8n saga",    slot: "specialist" },

  // Stores — public-facing conceptual names. Concrete engines
  // (Oxigraph · pgvector · LightRAG · APISIX) are internal choices and
  // never named on customer surfaces.
  { id: "oxigraph",         label: "Typed graph store",       sub: "Typed nodes + edges · SPARQL · ACID",  slot: "store",    protocol: "SPARQL" },
  { id: "pgvector",         label: "Vector store",            sub: "1024-d dense · tenant isolation",      slot: "store" },
  { id: "lightrag",         label: "Retrieval framework",     sub: "callbacks · strategies · patches",     slot: "store" },
  { id: "apisix",           label: "Model gateway",           sub: "Routed reasoning · embeddings · STT",  slot: "store" },
];

const LINKS: readonly MeshLink[] = [
  // External → protocols
  { from: "cockpit",  to: "agui", label: "SSE stream",       tone: "primary" },
  { from: "claude",   to: "mcp",  label: "JSON-RPC",         tone: "primary" },
  { from: "n8nIn",    to: "a2a",  label: "Webhook → A2A",    tone: "primary" },

  // Protocols → supervisor
  { from: "agui",     to: "supervisor", label: "events",     tone: "neutral" },
  { from: "a2a",      to: "supervisor", label: "tasks/send", tone: "neutral" },
  { from: "mcp",      to: "supervisor", label: "tools/call", tone: "neutral" },

  // Supervisor → specialists  (ADK transfer_to_agent)
  { from: "supervisor", to: "canvas",        label: "ADK transfer", tone: "ai" },
  { from: "supervisor", to: "constraint",    label: "ADK transfer", tone: "ai" },
  { from: "supervisor", to: "narrator",      label: "ADK transfer", tone: "ai" },
  { from: "supervisor", to: "substitution",  label: "ADK transfer", tone: "ai" },
  { from: "supervisor", to: "project_tasks", label: "ADK transfer", tone: "ai" },
  { from: "supervisor", to: "email_drafter", label: "ADK transfer", tone: "ai" },
  { from: "supervisor", to: "scheduling",    label: "ADK transfer", tone: "ai" },
  { from: "supervisor", to: "website",       label: "ADK transfer", tone: "ai" },
  { from: "supervisor", to: "estimate",      label: "ADK transfer", tone: "ai" },
  { from: "supervisor", to: "search",        label: "ADK transfer", tone: "ai" },
  { from: "supervisor", to: "voice",         label: "ADK transfer", tone: "ai" },
  { from: "supervisor", to: "change_order",  label: "ADK transfer", tone: "ai" },

  // Selected specialists → stores — wires use conceptual verbs so a
  // designer reading the mesh isn't bombarded with engine names.
  { from: "constraint",    to: "oxigraph",  label: "predicates",         tone: "neutral" },
  { from: "narrator",      to: "apisix",    label: "reasoning",          tone: "neutral" },
  { from: "search",        to: "lightrag",  label: "embed seam",         tone: "neutral" },
  { from: "search",        to: "pgvector",  label: "ANN cosine",         tone: "neutral" },
  { from: "search",        to: "oxigraph",  label: "grounding",          tone: "neutral" },
  { from: "email_drafter", to: "apisix",    label: "reasoning",          tone: "neutral" },
  { from: "voice",         to: "apisix",    label: "STT · TTS",          tone: "neutral" },
  { from: "estimate",      to: "oxigraph",  label: "graph walk",         tone: "neutral" },
  { from: "project_tasks", to: "oxigraph",  label: "milestone DAG",      tone: "neutral" },
  { from: "change_order",  to: "oxigraph",  label: "amend edges",        tone: "neutral" },
  { from: "canvas",        to: "oxigraph",  label: "Suggestion edges",   tone: "neutral" },
  { from: "canvas",        to: "lightrag",  label: "retrieval",          tone: "neutral" },
];

const SLOT_TONE: Record<Slot, string> = {
  external:   "ring-foyr/40    bg-foyr/5      text-foyr-deep",
  protocol:   "ring-violet-300 bg-violet-50   text-violet-800",
  supervisor: "ring-ink-900    bg-ink-950     text-cream",
  specialist: "ring-emerald-300 bg-emerald-50 text-emerald-800",
  store:      "ring-sky-300    bg-sky-50      text-sky-800",
};

const SLOT_ICON: Record<Slot, JSX.Element> = {
  external:   <Webhook  className="w-3 h-3" />,
  protocol:   <GitBranch className="w-3 h-3" />,
  supervisor: <Brain    className="w-3 h-3" />,
  specialist: <Brain    className="w-3 h-3" />,
  store:      <Database className="w-3 h-3" />,
};

function MeshNodeView({ data, selected }: { data: MeshNode; selected?: boolean }): JSX.Element {
  return (
    <div className="group cursor-default">
      <Handle type="target" position={Position.Left}  className="!w-2 !h-2 !bg-ink-300 !border-ink-300" />
      <Handle type="source" position={Position.Right} className="!w-2 !h-2 !bg-ink-300 !border-ink-300" />
      <div
        className={cls(
          "px-3 py-2 rounded-xl ring-1 shadow-[0_1px_2px_rgba(0,0,0,0.04)] min-w-[200px]",
          SLOT_TONE[data.slot],
          selected && "ring-2 !ring-foyr",
        )}
      >
        <div className="flex items-center gap-1.5">
          <span className="grid place-items-center w-4 h-4 rounded bg-cream/40 text-current shrink-0">
            {SLOT_ICON[data.slot]}
          </span>
          <span className="text-[12px] font-semibold leading-tight">{data.label}</span>
          {data.protocol ? (
            <span className="ml-auto pill text-[9px] bg-cream/30 border border-current/30 text-current font-mono">
              {data.protocol}
            </span>
          ) : null}
        </div>
        <div className={cls("text-[10px] leading-tight mt-0.5 opacity-80", data.slot === "supervisor" ? "text-cream/80" : "")}>
          {data.sub}
        </div>
      </div>
    </div>
  );
}

const nodeTypes: NodeTypes = { mesh: MeshNodeView as unknown as NodeTypes[string] };

function buildMesh(): { nodes: Node[]; edges: Edge[] } {
  // Five columns: external → protocol → supervisor → specialist → store.
  // External min-width 200 + breathing room → gap of 300 prevents the
  // "Dynamic integration layer" row from clipping into the protocol column.
  const colX: Record<Slot, number> = {
    external:   0,
    protocol:   320,
    supervisor: 600,
    specialist: 880,
    store:      1200,
  };
  const slotRows: Record<Slot, number> = { external: 0, protocol: 0, supervisor: 0, specialist: 0, store: 0 };
  const rowH = 88;
  const nodes: Node[] = NODES.map((n) => {
    const y = slotRows[n.slot]++ * rowH;
    return {
      id: n.id,
      type: "mesh",
      position: { x: colX[n.slot], y },
      data: n as unknown as Record<string, unknown>,
      sourcePosition: Position.Right,
      targetPosition: Position.Left,
      draggable: false,
    };
  });
  const edges: Edge[] = LINKS.map((l, i) => ({
    id: `m-${i}-${l.from}-${l.to}`,
    source: l.from,
    target: l.to,
    type: "smoothstep",
    animated: l.tone === "ai",
    label: l.label,
    labelStyle: { fontSize: 9, fill: "#7a766f", fontFamily: "ui-monospace, monospace" },
    labelBgStyle: { fill: "#f6f1e7" },
    labelBgPadding: [3, 1] as [number, number],
    labelBgBorderRadius: 6,
    markerEnd: { type: MarkerType.ArrowClosed, color: l.tone === "ai" ? "#c95c1a" : "#94908a", width: 14, height: 14 },
    style: { stroke: l.tone === "ai" ? "#c95c1a" : "#94908a", strokeWidth: l.tone === "ai" ? 1.5 : 1 },
  }));
  return { nodes, edges };
}

export function AgenticMesh(): JSX.Element {
  const { nodes, edges } = useMemo(() => buildMesh(), []);
  const specialistCount = NODES.filter((n) => n.slot === "specialist").length;
  const externalCount = NODES.filter((n) => n.slot === "external").length;
  return (
    <section className="space-y-3">
      <header className="flex items-end justify-between flex-wrap gap-2">
        <div>
          <div className="eyebrow inline-flex items-center gap-1.5">
            <GitBranch className="w-3 h-3" /> Agentic mesh
          </div>
          <h2 className="display-3 mt-0.5 text-ink-950">
            {specialistCount} agents · 3 protocols · {externalCount} entry points
          </h2>
          <p className="text-[12.5px] text-ink-500 mt-1 max-w-2xl">
            Every agent is real (mapped 1:1 to a module in <code className="font-mono">agent/foyr_agent/agents/</code>).
            Talks via A2A · AG-UI · MCP — same surface for the cockpit, Claude Desktop, n8n, and any external agent.
          </p>
        </div>
        <div className="flex flex-wrap gap-1.5">
          <span className="pill text-[10px] border ring-1 ring-foyr/40 bg-foyr/5 text-foyr-deep">external</span>
          <span className="pill text-[10px] border ring-1 ring-violet-300 bg-violet-50 text-violet-800">protocol</span>
          <span className="pill text-[10px] border ring-1 ring-ink-900 bg-ink-950 text-cream">supervisor</span>
          <span className="pill text-[10px] border ring-1 ring-emerald-300 bg-emerald-50 text-emerald-800">specialist</span>
          <span className="pill text-[10px] border ring-1 ring-sky-300 bg-sky-50 text-sky-800">store</span>
        </div>
      </header>
      <ExpandableGraphFrame
        title="Agentic mesh"
        subtitle={`${specialistCount} agents · 3 protocols · ${externalCount} entry points · A2A · AG-UI · MCP`}
        graph3d={{
          nodes: NODES.map<Graph3DNode>((n) => ({ id: n.id, label: n.label, kind: n.slot, description: n.sub })),
          links: LINKS.map<Graph3DLink>((l) => ({ source: l.from, target: l.to, label: l.label, emphasis: l.tone === "ai" ? "primary" : "neutral" })),
        }}
      >
      <div
        className="rounded-2xl border border-border/60 bg-[radial-gradient(circle_at_20%_20%,rgba(201,92,26,0.04),transparent_70%)]"
        style={{ height: 660 }}
      >
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          fitView
          fitViewOptions={{ padding: 0.08 }}
          panOnDrag
          zoomOnScroll
          minZoom={0.35}
          maxZoom={1.4}
          nodesDraggable={false}
          nodesConnectable={false}
          elementsSelectable={false}
          proOptions={{ hideAttribution: true }}
        >
          <Background gap={22} size={1} color="#ece7dc" />
        </ReactFlow>
      </div>
      </ExpandableGraphFrame>
    </section>
  );
}
